import {HiMiniXMark} from 'react-icons/hi2'
import {useTranslations} from 'use-intl'
import {useMemo} from 'react'
import clsx from 'clsx'
import {ProductFilters} from '~/components/product-list/Filter'
import {FilterOptions} from '~/gql/graphql'

type FilterBadgeListProps = {
    className?: string
    filter?: FilterOptions
    activeFilter?: ProductFilters
    onChange: (filter: ProductFilters) => void
}

const FilterBadgeList = ({className, filter, activeFilter, onChange}: FilterBadgeListProps) => {
    const activeDepartment = useMemo(() => {
        const categories = filter?.categories
        if (!activeFilter?.departmentId || !categories) {
            return undefined
        }

        const department = categories.find(
            (department) => department?.id === activeFilter.departmentId
        )
        return department ?? undefined
    }, [activeFilter?.departmentId, filter?.categories])

    const activeCategory = useMemo(() => {
        const subCategories = activeDepartment?.subCategories

        if (!activeFilter?.categoryId || !subCategories) {
            return undefined
        }

        const category = subCategories.find((category) => category?.id === activeFilter.categoryId)
        return category ?? undefined
    }, [activeDepartment?.subCategories, activeFilter?.categoryId])

    const activeAppearance = useMemo(() => {
        const appearances = filter?.appearances
        if (!activeFilter?.appearanceId || !appearances) {
            return undefined
        }

        const appearance = appearances.find(
            (appearance) => appearance?.id === activeFilter.appearanceId
        )
        return appearance ?? undefined
    }, [filter?.appearances, activeFilter?.appearanceId])

    const activeSize = useMemo(() => {
        const sizes = filter?.sizes

        if (!activeFilter?.sizeId || !sizes) {
            return undefined
        }

        const size = sizes.find((size) => size?.id === activeFilter.sizeId)
        return size ?? undefined
    }, [filter?.sizes, activeFilter?.sizeId])

    if (!activeDepartment && !activeCategory && !activeAppearance && !activeSize) {
        return null
    }

    return (
        <ul className={clsx('flex flex-row gap-3 items-center flex-wrap', className)}>
            {!!activeDepartment && (
                <li className="overflow-hidden flex-shrink">
                    <FilterBadge
                        name={activeDepartment.name ?? ''}
                        onDeselect={() => onChange({})}
                    />
                </li>
            )}
            {!!activeCategory && (
                <li className="overflow-hidden flex-shrink">
                    <FilterBadge
                        name={activeCategory.name ?? ''}
                        onDeselect={() => onChange({...activeFilter, categoryId: undefined})}
                    />
                </li>
            )}
            {!!activeAppearance && (
                <li className="overflow-hidden flex-shrink">
                    <FilterBadge
                        name={activeAppearance.name ?? ''}
                        onDeselect={() => onChange({...activeFilter, appearanceId: undefined})}
                    />
                </li>
            )}
            {!!activeSize && (
                <li className="overflow-hidden flex-shrink">
                    <FilterBadge
                        name={activeSize.name ?? ''}
                        onDeselect={() => onChange({...activeFilter, sizeId: undefined})}
                    />
                </li>
            )}
        </ul>
    )
}

type FilterBadgeProps = {
    name: string
    onDeselect: () => void
}

const FilterBadge = ({name, onDeselect}: FilterBadgeProps) => {
    const t = useTranslations('productList')
    return (
        <button
            title={t('removeFilter', {filter: name})}
            className="inline-flex flex-row items-center gap-2 border rounded px-2 py-1 text-sm w-full hover:bg-gray-700/60 transition-colors duration-200 ease-in-out"
            onClick={onDeselect}
            aria-label={t('removeFilter', {filter: name})}
        >
            <span className="truncate capitalize">{name}</span>
            <HiMiniXMark className="size-4 flex-shrink-0" />
        </button>
    )
}

export default FilterBadgeList
